<script setup lang="ts">
import { ref } from 'vue';
import { AkModal, AkInput, AkHeading4, AkParagraph, AkButton } from '@ankorstore/design-system';
import { toRefs, watchEffect } from 'vue';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { nextTick } from 'vue';
import icons from './icons';
import { getContent } from './templates';
import { AksInsertLinkButton, PLUGIN_KEY } from './config';
import { EditorInteractionManager } from '../types';
import { useDomainValidation } from '@bc/shared/composables/use-domain-validation/use-domain-validation';
import { CaptureErrorFunc } from '../../use-error-logger';

enum ModalMode {
  Insert = 'insert',
  Edit = 'edit',
  Closed = 'closed',
}

defineOptions({ name: 'AksInsertLinkPlugin' });

const props = defineProps<{
  id: string;
  editorInteractionManager: EditorInteractionManager;
  captureError: CaptureErrorFunc;
}>();

const emit = defineEmits(['toolbarButtonClicked', 'contentInserted'])

const { id, editorInteractionManager: im } = toRefs(props);
const modal = ref<typeof AkModal>(null);
const { t } = useI18n();
const { validateDomain } = useDomainValidation();

const labelInput = ref<typeof AkInput>(null);
const selectedNode = ref<Element | null>(null);
const modalMode = ref<ModalMode>(ModalMode.Closed);

const label = ref('');
const url = ref('');
const errors = ref<{
  label: string;
  url: string;
}>({
  label: '',
  url: ''
});

const pluginId = computed(() => id.value + '_' + PLUGIN_KEY);
const validateForm = () => {
  let isValid = true;

  if (!label.value) {
    errors.value.label = t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.error.required');
    isValid = false;
  }

  const urlValidation = validateDomain(url.value);
  if (!urlValidation.isValid) {
    errors.value.url = urlValidation.errorMessage;
    isValid = false;
  }

  return isValid;
};

const clearForm = () => {
  label.value = '';
  url.value = '';

  clearErrors('label');
  clearErrors('url');
};

const register = () => {
  im.value.registerPlugin(pluginId.value, ({ addButton, addMenuItem, addContextMenu, addIcons }) => {
      addIcons(icons);

      addButton(AksInsertLinkButton.InsertLink, {
        text: t('account.brands.createCampaigns.editor.toolbar.insertLink.label'),
        icon: 'link',
        onAction: () => {
          const selectedText = im.value.getSelectedText();

          // Prefill label if user clicked 'Link' button while highlighting text
          if (selectedText) {
            label.value = selectedText;
          }

          openModal({ mode: ModalMode.Insert });
          emit('toolbarButtonClicked', PLUGIN_KEY);
        }
      });

      addMenuItem(AksInsertLinkButton.EditLink, {
        text: t('account.brands.createCampaigns.editor.toolbar.insertLink.contextMenu.edit'),
        icon: 'edit-link',
        onAction: () => {
          if (selectedNode.value) {
            label.value = selectedNode.value?.textContent?.trim();
            url.value = selectedNode.value?.getAttribute('href');

            openModal({ mode: ModalMode.Edit });
          }
        }
      });

      addMenuItem(AksInsertLinkButton.RemoveLink, {
        text: t('account.brands.createCampaigns.editor.toolbar.insertLink.contextMenu.remove'),
        icon: 'remove-link',
        onAction: () => {
          if (selectedNode.value) {
            const text = selectedNode.value?.textContent?.trim();
            im.value.replaceContent(text);
          }
        }
      });

      addMenuItem(AksInsertLinkButton.OpenLink, {
        text: t('account.brands.createCampaigns.editor.toolbar.insertLink.contextMenu.open'),
        icon: 'open-link',
        onAction: () => {
          if (selectedNode.value) {
            window.open(selectedNode.value?.getAttribute('href'), '_blank');
          }
        }
      });

      // Overwrite the default link context menu
      addContextMenu('link', {
        update: (node) => {
          if (node?.hasAttribute(`data-${PLUGIN_KEY}`)) {
            selectedNode.value = node;
            im.value.selectNode(selectedNode.value);
            return [AksInsertLinkButton.EditLink, AksInsertLinkButton.RemoveLink, AksInsertLinkButton.OpenLink].join(' ');
          }

          return null;
        }
      });
    });
};

const handleSubmit = () => {
  if (!validateForm() && im.value) {
    return;
  }

  const content = getContent(label.value, url.value, PLUGIN_KEY);

  if (modalMode.value === ModalMode.Edit) {
    im.value.replaceContent(content);
  } else {
    im.value.insertContent(content);
    emit('contentInserted', PLUGIN_KEY);
  }

  closeModal();
};

const openModal = async (options: { mode?: ModalMode }) => {
  modalMode.value = options.mode ?? ModalMode.Insert;
  modal.value.openModal();
  await nextTick();
  labelInput.value?.focusInput();
};

const closeModal = () => {
  modalMode.value = ModalMode.Closed;
  modal.value.close();
  clearForm();
};

const clearErrors = (field: 'label' | 'url') => {
  errors.value[field] = '';
};

watchEffect(() => {
  if (modal.value && im.value && pluginId.value) {
    register();
  }
});

</script>
<template>
  <Teleport to="body">
    <AkModal
      ref="modal"
      data-testid="insert-link-modal"
      @cancelled="closeModal"
    >
      <div class="insert-link-modal__content">
        <AkHeading4 class="insert-link-modal__title">
          {{ t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.title') }}
        </AkHeading4>
        <AkParagraph class="insert-link-modal__description">
          {{ t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.description') }}
        </AkParagraph>
        <form @submit.prevent="handleSubmit">
          <AkInput
            ref="labelInput"
            v-model="label"
            :placeholder="t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.label.placeholder')"
            :label="t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.label.label')"
            class="insert-link-modal__input"
            :errors="errors.label"
            data-testid="label-input"
            @input="clearErrors('label')"
          />
          <AkInput
            v-model="url"
            :placeholder="t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.url.placeholder')"
            :label="t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.url.label')"
            class="insert-link-modal__input"
            :errors="errors.url"
            data-testid="url-input"
            @input="clearErrors('url')"
          />
          <div class="insert-link-modal__actions">
            <AkButton
              outlined
              @click="closeModal"
            >
              {{ t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.cancel') }}
            </AkButton>
            <AkButton
              type="submit"
              @click.prevent="handleSubmit"
            >
              {{ modalMode === ModalMode.Edit ? t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.submitUpdate') : t('account.brands.createCampaigns.editor.toolbar.insertLink.modal.submit') }}
            </AkButton>
          </div>
        </form>
      </div>
    </AkModal>
  </Teleport>
</template>

<style scoped lang="scss">
.insert-link-modal__content {
  @apply ds-text-left;

  .insert-link-modal__title {
    @apply ds-mb-4 ds-text-center;
  }

  .insert-link-modal__description {
    @apply ds-mb-4 ds-text-center;
  }

  .insert-link-modal__input {
    @apply ds-mb-4;
  }

  .insert-link-modal__actions {
    @apply ds-flex ds-justify-center ds-gap-4 ds-mt-1;
  }
}
</style>
